import React, { useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Chip,
} from '@mui/material'
import {
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
} from '@mui/icons-material'
import { format } from 'date-fns'

interface Document {
  id: string
  type: string
  createdOn: string
  status?: 'pending' | 'authorized' | 'rejected'
  downloadUrl?: string
}

interface DocumentsSectionProps {
  documents?: Document[]
  loading?: boolean
  onUpload?: () => void
  onDownload?: (documentId: string) => Promise<void>
}

const DocumentsSection: React.FC<DocumentsSectionProps> = ({
  documents = [],
  loading = false,
  onUpload,
  onDownload,
}) => {
  const [downloadingId, setDownloadingId] = useState<string | null>(null)

  const handleDownload = async (document: Document) => {
    if (!onDownload || downloadingId) return

    setDownloadingId(document.id)
    try {
      await onDownload(document.id)
    } catch (error) {
      console.error('Failed to download document:', error)
    } finally {
      setDownloadingId(null)
    }
  }

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'authorized':
        return 'success'
      case 'pending':
        return 'warning'
      case 'rejected':
        return 'error'
      default:
        return 'default'
    }
  }

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'authorized':
        return 'Authorized'
      case 'pending':
        return 'Authorization Pending'
      case 'rejected':
        return 'Rejected'
      default:
        return ''
    }
  }

  return (
    <Card>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6">Documents</Typography>
          {onUpload && (
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={onUpload}
              size="small"
            >
              Upload
            </Button>
          )}
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={24} />
          </Box>
        ) : documents.length === 0 ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              No documents available
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                    Type
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                    Created On
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, color: 'primary.main' }}
                  >
                    Download
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map(document => (
                  <TableRow key={document.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{document.type}</Typography>
                        {document.status && (
                          <Chip
                            label={getStatusLabel(document.status)}
                            color={getStatusColor(document.status)}
                            size="small"
                            sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(new Date(document.createdOn), 'MM/dd/yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleDownload(document)}
                        disabled={downloadingId === document.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        {downloadingId === document.id ? (
                          <CircularProgress size={18} />
                        ) : (
                          <DownloadIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  )
}

export default DocumentsSection

